import React, { useEffect, useState } from 'react';
import { schemaService } from '../../services/schemaService';
import { FieldDefinitions } from '../../types/schema';
import { GenericDynamicForm } from './GenericDynamicForm';

interface DynamicFormLoaderProps {
  domain: string;
  schemaId?: string;  // Optional: if provided, load this specific schema instead of active one
  data: Record<string, any>;
  provenance?: Record<string, { snippet: string; page: number }>;
  fieldConfidences?: Record<string, { confidence: 'high' | 'medium' | 'low'; reason?: string | null }>;
  onChange: (data: Record<string, any>) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting?: boolean;
  onProvenanceClick?: (fieldPath: string) => void;
}

/**
 * Loads a schema and renders a dynamic form based on field_definitions.
 * If schemaId is provided, loads that specific schema.
 * Otherwise, loads the active schema for the domain.
 */
export function DynamicFormLoader({
  domain,
  schemaId,
  data,
  provenance,
  fieldConfidences,
  onChange,
  onSubmit,
  isSubmitting,
  onProvenanceClick,
}: DynamicFormLoaderProps) {
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadSchema() {
      try {
        setIsLoading(true);
        setError(null);

        // Load specific schema if schemaId is provided, otherwise load active schema
        const schema = schemaId
          ? await schemaService.get(schemaId)
          : await schemaService.getActive(domain);

        if (!schema.field_definitions) {
          throw new Error(`Schema has no field_definitions`);
        }

        setFieldDefinitions(schema.field_definitions);
      } catch (err: any) {
        console.error('Failed to load schema:', err);
        setError(err.message || 'Failed to load form schema');
      } finally {
        setIsLoading(false);
      }
    }

    loadSchema();
  }, [domain, schemaId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 font-medium">Erreur de chargement</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!fieldDefinitions) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">
          Aucune définition de formulaire disponible pour le domaine "{domain}"
        </p>
      </div>
    );
  }

  return (
    <GenericDynamicForm
      fieldDefinitions={fieldDefinitions}
      data={data}
      provenance={provenance}
      fieldConfidences={fieldConfidences}
      onChange={onChange}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      onProvenanceClick={onProvenanceClick}
    />
  );
}
